import React, { useState, useEffect } from "react";
import { open_api_with_cookies } from "../../../Utils/network";
import FilterChip from "../../../components/Chip/FilterChip";
import LoadingOverlay from "../../../components/Loading/LoadingOverlay";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import "./AIUsage.css";

const dayOptions = ["7 Days", "14 Days", "30 Days", "60 Days", "90 Days"];
const dayMap = {
  "7 Days": 7,
  "14 Days": 14,
  "30 Days": 30,
  "60 Days": 60,
  "90 Days": 90,
};

const formatDateTime = (value) => {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Never";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const AIUsage = () => {
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [timeframe, setTimeframe] = useState("30 Days");

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const days = dayMap[timeframe];
        const res = await open_api_with_cookies.get(
          `/myburgoai/admin/usage/?days=${days}`
        );
        setData(res.data);
      } catch (error) {
        console.error("Error fetching AI usage:", error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [timeframe]);

  if (isLoading) return <LoadingOverlay />;
  if (!data) return <div className="ai-usage">Failed to load data.</div>;

  const { daily_stats, period_totals, user_breakdown, model_usage } = data;

  const chartData = (daily_stats || []).map((d) => ({
    date: new Date(d.date).toLocaleDateString("en-US", {
      day: "numeric",
      month: "short",
    }),
    tokens: d.total_tokens,
    messages: d.messages_sent + d.messages_received,
    cost: d.total_cost,
    users: d.unique_users,
  }));

  return (
    <div className="ai-usage">
      <div className="ai-usage-header">
        <h2>AI Usage & Costs</h2>
        <div style={{ width: 160 }}>
          <FilterChip
            options={dayOptions}
            selected={timeframe}
            onSelect={setTimeframe}
          />
        </div>
      </div>

      {/* Period Totals */}
      <div className="ai-usage-totals">
        <div className="ai-usage-total-card">
          <div className="usage-label">Total Tokens</div>
          <div className="usage-value">
            {(period_totals.total_tokens || 0).toLocaleString()}
          </div>
          <div className="usage-sub">Last {period_totals.days} days</div>
        </div>
        <div className="ai-usage-total-card">
          <div className="usage-label">Messages Sent</div>
          <div className="usage-value">
            {(period_totals.messages_sent || 0).toLocaleString()}
          </div>
          <div className="usage-sub">
            {(period_totals.messages_received || 0).toLocaleString()} received
          </div>
        </div>
        <div className="ai-usage-total-card">
          <div className="usage-label">Estimated Cost</div>
          <div className="usage-value">
            ${(period_totals.total_cost || 0).toFixed(4)}
          </div>
          <div className="usage-sub">USD estimated</div>
        </div>
        <div className="ai-usage-total-card">
          <div className="usage-label">Active Users</div>
          <div className="usage-value">{period_totals.unique_users || 0}</div>
          <div className="usage-sub">
            {(period_totals.images_uploaded || 0)} images uploaded,{" "}
            {(period_totals.images_generated || 0)} generated
          </div>
        </div>
      </div>

      {/* Token Usage Chart */}
      <div className="ai-usage-chart-section">
        <h3>Daily Token Usage</h3>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart
            data={chartData}
            margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
          >
            <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
            <XAxis dataKey="date" tick={{ fill: "#666", fontSize: 12 }} />
            <YAxis tick={{ fill: "#666" }} />
            <Tooltip
              contentStyle={{
                backgroundColor: "rgba(255,255,255,0.95)",
                border: "none",
                borderRadius: "8px",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              }}
              formatter={(value, name) => {
                if (name === "tokens") return [value.toLocaleString(), "Tokens"];
                if (name === "cost") return [`$${value.toFixed(4)}`, "Cost"];
                return [value, name];
              }}
            />
            <Area
              type="monotone"
              dataKey="tokens"
              stroke="#7045fb"
              fill="url(#tokenGradient)"
              strokeWidth={2}
            />
            <defs>
              <linearGradient id="tokenGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#7045fb" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#7045fb" stopOpacity={0} />
              </linearGradient>
            </defs>
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Model Usage */}
      <div className="ai-usage-model-section">
        <div className="ai-usage-model-card">
          <h3>Usage by Model</h3>
          <div className="model-stat-row">
            <div>Model</div>
            <div>Input Tokens</div>
            <div>Output Tokens</div>
            <div>Messages</div>
          </div>
          {(model_usage || []).map((m, i) => (
            <div key={i} className="model-stat-row">
              <div style={{ fontWeight: 500 }}>{m.model}</div>
              <div>{(m.input_tokens || 0).toLocaleString()}</div>
              <div>{(m.output_tokens || 0).toLocaleString()}</div>
              <div>{(m.message_count || 0).toLocaleString()}</div>
            </div>
          ))}
        </div>

        {/* Daily Users Chart */}
        <div className="ai-usage-model-card">
          <h3>Daily Active Users</h3>
          <ResponsiveContainer width="100%" height={250}>
            <AreaChart
              data={chartData}
              margin={{ top: 10, right: 10, left: 0, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
              <XAxis dataKey="date" tick={{ fill: "#666", fontSize: 12 }} />
              <YAxis tick={{ fill: "#666" }} allowDecimals={false} />
              <Tooltip
                contentStyle={{
                  backgroundColor: "rgba(255,255,255,0.95)",
                  border: "none",
                  borderRadius: "8px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                }}
              />
              <Area
                type="monotone"
                dataKey="users"
                stroke="#05cd99"
                fill="url(#userGradient)"
                strokeWidth={2}
              />
              <defs>
                <linearGradient id="userGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#05cd99" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#05cd99" stopOpacity={0} />
                </linearGradient>
              </defs>
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* User Breakdown */}
      <div className="ai-usage-users-table">
        <h3>Usage by User</h3>
        <div className="user-usage-header">
          <div>#</div>
          <div>User</div>
          <div>Messages</div>
          <div>Tokens</div>
          <div>Cost</div>
          <div>Days</div>
          <div>Last App Activity</div>
        </div>
        {(user_breakdown || []).map((u, i) => (
          <div key={u.user_id} className="user-usage-row">
            <div>
              <span
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  background: "#f0ecff",
                  color: "#7045fb",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 600,
                  fontSize: 12,
                }}
              >
                {i + 1}
              </span>
            </div>
            <div className="usage-user-identity">
              <span>{u.name || "Anonymous"}</span>
              {u.email && <small>{u.email}</small>}
              {u.phone && (
                <small>{[u.isd_code, u.phone].filter(Boolean).join(" ")}</small>
              )}
            </div>
            <div>{(u.total_messages || 0).toLocaleString()}</div>
            <div>{(u.total_tokens || 0).toLocaleString()}</div>
            <div>${(u.total_cost || 0).toFixed(4)}</div>
            <div>{u.days_active}</div>
            <div className="usage-user-activity">
              <small className={u.app_installed ? "app-installed" : ""}>
                {u.app_installed ? "App installed" : "Installation not observed"}
              </small>
              <span>{formatDateTime(u.last_app_used_at)}</span>
              {u.last_app_opened_at && (
                <small>Opened {formatDateTime(u.last_app_opened_at)}</small>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AIUsage;
